import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getAllTicket, postRaiseTicketV2, deleteTicket, editOrganisationUser } from '../../../services/userService';
import NoData from '../../../components/common/NoData';

function Ticket() {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [ticketData, setTicketData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalPages: 1, totalCount: 0 });

  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [subject, setSubject] = useState('');
  const [summary, setSummary] = useState('');
  const [errors, setErrors] = useState({ subject: '', summary: '' });

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [raising, setRaising] = useState(false);

  useEffect(() => {
    getTicketData();
  }, [page, limit, searchTerm]);

  const getTicketData = async () => {
    try {
      const payload = { page, limit, search: searchTerm };
      const response = await getAllTicket(payload);
      if (response.success && response.data) {
        setTicketData(response.data.tickets);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching ticket data:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-yellow-500 bg-yellow-50 text-yellow-600">
            <Icon icon="mdi:clock-outline" className="text-base" />
            Pending
          </span>
        );
      case 'Solved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-green-500 bg-green-50 text-green-600">
            <Icon icon="mdi:check-circle-outline" className="text-base" />
            Solved
          </span>
        );
      case 'In Progress':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-blue-500 bg-blue-50 text-blue-600">
            <Icon icon="mdi:progress-clock" className="text-base" />
            In Progress
          </span>
        );
      case 'Cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-red-500 bg-red-50 text-red-600">
            <Icon icon="mdi:close-circle-outline" className="text-base" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-gray-500 bg-gray-50 text-gray-600">
            <Icon icon="mdi:help-circle-outline" className="text-base" />
            Unknown
          </span>
        );
    }
  };

  const handleRaiseTicket = async () => {
    const newErrors = { subject: '', summary: '' };

    if (!subject.trim()) newErrors.subject = 'Subject is required';
    if (!summary.trim()) newErrors.summary = 'Summary is required';

    setErrors(newErrors);
    if (Object.values(newErrors).some(msg => msg)) return;

    setRaising(true);
    const payload = { subject, description: summary };

    setTimeout(async () => {
      try {
        const response = await postRaiseTicketV2(payload);
        if (response.success) {
          toast.success('Ticket raised successfully');
          getTicketData();
          setShowModal(false);
          setSubject('');
          setSummary('');
        } else toast.error(response.error_msg || 'Something went wrong');
      } catch (error) {
        console.error('Error raising ticket:', error);
        toast.error('Internal server error');
      } finally {
        setRaising(false);
      }
    }, 2000);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);

    setTimeout(async () => {
      try {
        const response = await deleteTicket(ticketToDelete.ticket_id);
        if (response.success) {
          toast.success('Ticket deleted successfully');
          getTicketData();
        } else {
          toast.error(response.error_msg || 'Something went wrong');
        }
      } catch (err) {
        console.error('Delete error:', err);
        toast.error('Internal server error');
      } finally {
        setDeleting(false);
        setShowDeleteModal(false);
        setTicketToDelete(null);
      }
    }, 2000);
  };


  return (
    <>
      <div className="flex flex-col py-3">
        <div className="w-full mb-3">
          <div className="flex flex-wrap justify-between items-center">
            <h4 className="mb-2 md:mb-0 text-xl font-semibold">Ticket List</h4>
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg whitespace-nowrap w-auto"
              onClick={() => setShowModal(true)}
            >
              Raise Ticket
            </button>
          </div>
        </div>


        <div className="w-full">
          <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
            <div className="flex w-auto">
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden md:flex">Page </span>
              <span className="hidden md:flex">Size:</span>

              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {[10, 50, 100].map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full border-collapse border border-gray-300">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="whitespace-nowrap px-2.5 py-2 border border-gray-300">SL No.</th>
                  <th className="whitespace-nowrap px-2.5 py-2 border border-gray-300">Ticket ID</th>
                  <th className="whitespace-nowrap px-2.5 py-2 border border-gray-300 min-w-[250px]">Subject</th>
                  <th className="whitespace-nowrap px-2.5 py-2 border border-gray-300 min-w-[250px]">Response</th>
                  <th className="whitespace-nowrap px-2.5 py-2 border border-gray-300">Status</th>
                  <th className="whitespace-nowrap px-2.5 py-2 border border-gray-300">Submitted Date</th>
                  <th className="whitespace-nowrap px-2.5 py-2 border border-gray-300 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {ticketData.length > 0 ? (
                  ticketData.map((item, index) => (
                    <tr key={item.ticket_id} className="hover:bg-gray-50 even:bg-gray-100">
                      <td className="text-center align-middle border border-gray-300 px-2.5 py-2">{(page - 1) * limit + index + 1}</td>
                      <td className="align-middle border border-gray-300 px-2.5 py-2">{item.ticket_id}</td>
                      <td className="align-middle border border-gray-300 px-2.5 py-2">
                        <div className="line-clamp-2" title={item.subject}>
                          {item.subject}
                        </div>
                      </td>
                      <td className="align-middle border border-gray-300 px-2.5 py-2">
                        <div className="line-clamp-2" title={item.response || 'No response yet'}>
                          {item.response || 'No response yet'}
                        </div>
                      </td>
                      <td className="align-middle border border-gray-300 px-2.5 py-2">{getStatusBadge(item.status)}</td>
                      <td className="align-middle border border-gray-300 px-2.5 py-2">{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td className="flex justify-center items-center gap-2 align-middle border border-gray-300 px-2.5 py-2">
                        <button className="border border-primary text-primary hover:bg-primary hover:text-white font-medium py-1 px-2 rounded-lg text-sm"
                          onClick={() => { setSelectedTicket(item); setShowViewModal(true); }}
                          style={{ width: '32px', height: '32px', padding: '4px' }}
                        >
                          <Icon icon="mdi:eye" />
                        </button>
                        <button className="border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-medium py-1 px-2 rounded-lg text-sm"
                          onClick={() => { setTicketToDelete(item); setShowDeleteModal(true); }}
                          style={{ width: '32px', height: '32px', padding: '4px' }}
                        >
                          <Icon icon="mdi:delete" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center border border-gray-300 px-2.5 py-2">No tickets found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center md:justify-between items-center flex-wrap gap-2 mt-3">
            <div>Showing {(page - 1) * limit + 1} to {Math.min(page * limit, pagination.totalCount)} of {pagination.totalCount} entries</div>
            <div className="flex gap-2">
              <button className="border border-gray-500 text-gray-700 hover:bg-gray-100 font-medium py-1 px-3 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
              <span className="py-1">Page {page} of {pagination.totalPages}</span>
              <button className="border border-gray-500 text-gray-700 hover:bg-gray-100 font-medium py-1 px-3 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowViewModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Ticket Details</h3>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">×</button>
            </div>
            <div className="p-4">
              {selectedTicket && (
                <div className="flex flex-col gap-3">
                  <div><strong className="font-semibold">Ticket ID:</strong><div className="mt-1">{selectedTicket.ticket_id}</div></div>
                  <div><strong className="font-semibold">Subject:</strong><div className="mt-1">{selectedTicket.subject}</div></div>
                  <div><strong className="font-semibold">Summary:</strong><div className="mt-1">{selectedTicket.description}</div></div>
                  <div><strong className="font-semibold">Response:</strong><div className="mt-1">{selectedTicket.response || 'No response yet'}</div></div>
                  <div><strong className="font-semibold">Status:</strong><div className="mt-1">{selectedTicket.status}</div></div>
                  <div><strong className="font-semibold">Submitted Date:</strong><div className="mt-1">{new Date(selectedTicket.createdAt).toLocaleDateString()}</div></div>
                </div>
              )}
            </div>
            <div className="flex justify-end p-4 border-t border-gray-200">
              <button className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg" onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Confirm Deletion</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none" disabled={deleting}>×</button>
            </div>
            <div className="p-4">
              Are you sure you want to delete this ticket?
              <br />
              <strong className="font-semibold">Ticket ID: {ticketToDelete?.ticket_id}</strong>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
              <button className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50" onClick={() => setShowDeleteModal(false)} disabled={deleting}>Cancel</button>
              <button className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 flex items-center gap-2" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Deleting...</>) : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Raise Ticket Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Raise New Ticket</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none" disabled={raising}>×</button>
            </div>
            <div className="p-4">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  placeholder="Enter subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.subject ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
                <textarea
                  rows={4}
                  placeholder="Enter summary"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.summary ? 'border-red-500' : 'border-gray-300'}`}
                  maxLength={2000}
                />
                {errors.summary && <p className="text-red-500 text-sm mt-1">{errors.summary}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
              <button className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50" onClick={() => setShowModal(false)} disabled={raising}>Close</button>
              <button className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 flex items-center gap-2" onClick={handleRaiseTicket} disabled={raising}>
                {raising ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Raising...</>) : 'Raise Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Ticket;
